import axios from "../utils/axios";
import { decryptData, encryptData, generateKey } from "../utils/index.jsx";
import { derivePasswordKey, generateAndWrapKeks, hashPassword, isArgon2Auth, unwrapKek, wrapKek } from "../utils/crypto";
import {
    deriveInvitationSecrets,
    generateRootToken,
    mintWrapSalt,
    unwrapInvestigationKey,
    wrapInvestigationKey,
} from "../utils/invitationWrap";
import { AcceptFragment, buildAcceptLink } from "../utils/invitationLink";

/**
 * SHER-803 on develop-skincair - both halves of the per-invitation key-handoff (SHER-769 decision
 * doc, 3.3 and 3.6), wired to THIS line's key model.
 *
 * Same contract as develop's module, with one material difference: here the data is opened by
 * three per-investigation KEKs (personal_data, images, consents), not by one investigation key.
 * So the inviter wraps each KEK under the invitation's `wrapKey` (AES-GCM, the same primitive as
 * crypto.ts) alongside the v1 key; the API keeps them with the invitation and shows them only to the
 * holder of the link's proof (`/invitation/status`). The invitee opens them with the link and sends
 * them back re-wrapped under their own password-derived key (PDK) IN the request that spends the
 * token - `register/v2` for a new account (registering IS accepting on this line), `answer/v2` for
 * an existing one - so the API writes them in the same transaction and no membership is ever
 * accepted with its KEKs unopened.
 *
 * WHAT NEVER LEAVES THIS MODULE: `S`, the raw v1 key and the raw KEKs (except into this tab's
 * localStorage, where the app already keeps raw KEKs for the signed-in session). Every request
 * carries `authProof`, a salt, `siw1:` blobs, AES-GCM wraps or a CryptoJS personal wrap.
 * `invitations.test.ts` captures every request and console line and searches them for the secrets.
 *
 * Nothing here logs. On failure it returns a reason instead.
 */

const API = () => import.meta.env.VITE_APP_API_URL;
const authHeaders = () => ({ headers: { Authorization: localStorage.getItem("jwt") } });

/** `share_status` value the API uses for an accepted invitation. */
const ACCEPTED = 2;

export const KEK_CATEGORIES = ["personal_data", "images", "consents"] as const;

export interface InviteeRequest {
    email: string;
    roleId: number;
}

export interface WrappedKekEntry {
    category: string;
    nonce: string;
    ciphertext: string;
}

export interface TokenInviteePayload extends InviteeRequest {
    keyResearcherInvestigation: string;
    authProof: string;
    wrapSalt: string;
    wrappedKeks?: WrappedKekEntry[];
}

/** Raw KEKs the inviter holds, base64, by category (what the app keeps as `kek_<inv>_<cat>`). */
export type RawKeks = Partial<Record<string, string>>;

const fromB64 = (value: string) => Uint8Array.from(atob(value), (c) => c.charCodeAt(0));
const toB64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...Array.from(bytes)));

/** The KEKs the signed-in inviter holds for this investigation, as the app stores them. */
export function readStoredKeks(investigationUuid: string, storage: Storage = window.localStorage): RawKeks {
    const keks: RawKeks = {};
    for (const category of KEK_CATEGORIES) {
        const value = storage.getItem(`kek_${investigationUuid}_${category}`);
        if (value) {
            keks[category] = value;
        }
    }
    return keks;
}

/**
 * One invitee's share entry plus the secret that must stay behind. `rootToken` is returned to the
 * caller only so it can be put in that invitee's link; it is not part of `payload`.
 *
 * Refuses when the inviter holds NOTHING to hand over - no v1 key and no KEK. On this line the v1
 * key is empty for every argon2 investigation, so an empty v1 key alone is normal; an inviter with
 * neither has lost their session's keys, and inviting would hand the invitee nothing (SHER-344).
 */
export async function prepareTokenInvitee(
    invitee: InviteeRequest,
    rawKeyInvestigation: string,
    keks: RawKeks = {},
): Promise<{ payload: TokenInviteePayload; rootToken: string }> {
    const kekEntries = Object.entries(keks).filter(([, value]) => !!value) as [string, string][];
    if (!rawKeyInvestigation && kekEntries.length === 0) {
        throw new Error("SHER-803: refusing to invite with no investigation key and no KEKs - the inviter's keys are unavailable; sign in again.");
    }
    const rootToken = generateRootToken();
    const wrapSalt = mintWrapSalt();
    const { wrapKey, authProof } = await deriveInvitationSecrets(rootToken, wrapSalt);
    const keyResearcherInvestigation = await wrapInvestigationKey(wrapKey, rawKeyInvestigation || "");
    const payload: TokenInviteePayload = { email: invitee.email, roleId: invitee.roleId, keyResearcherInvestigation, authProof, wrapSalt };
    if (kekEntries.length > 0) {
        payload.wrappedKeks = [];
        for (const [category, value] of kekEntries) {
            payload.wrappedKeks.push({ category, ...(await wrapKek(fromB64(value), wrapKey)) });
        }
    }
    return { payload, rootToken };
}

export interface InvitationLink {
    email: string;
    link: string;
    expiresAt: string;
}

export interface ShareWithTokensResult {
    status: number;
    errorCode?: number;
    errorMessage?: string;
    sharedResearchers?: unknown[];
    /** One per invitee, for the inviter to deliver. Present only on success. */
    links?: InvitationLink[];
    /** Invitees the API enrolled but for whom no link could be built - they need a Resend. */
    missingLinks?: string[];
}

/**
 * Invite, and build each invitee's accept link. The links exist only in the returned value: the
 * API cannot rebuild them (it never had `S`), so a caller that drops them has to re-invite.
 * `/share/v2` when the entries carry KEKs, `/share` otherwise - the same choice the screen made
 * before this ticket.
 */
export async function shareInvestigationWithTokens(
    investigationUuid: string,
    invitees: InviteeRequest[],
    rawKeyInvestigation: string,
    origin: string,
    keks: RawKeks = {},
): Promise<ShareWithTokensResult> {
    const prepared = [];
    for (const invitee of invitees) {
        prepared.push(await prepareTokenInvitee(invitee, rawKeyInvestigation, keks));
    }
    const endpoint = prepared.some((p) => p.payload.wrappedKeks) ? "/share/v2" : "/share";
    const response = await axios.post(
        `${API()}/researcher/investigation/${investigationUuid}${endpoint}`,
        { listResearchers: prepared.map((p) => p.payload) },
        authHeaders(),
    );
    const data = response.data || {};
    if (response.status !== 200) {
        return { status: response.status, errorCode: data.errorCode, errorMessage: data.errorMessage };
    }

    // The API issues invitations in request order; pair by position, and confirm by email so a
    // mismatch can never put one person's secret in another person's link. The invitations are
    // already committed, so nothing here may throw: an unpaired invitee is reported (Resend).
    const issuedList: { email: string; invitationUuid: string; expiresAt: string }[] = data.invitations || [];
    const links: InvitationLink[] = [];
    const missingLinks: string[] = [];
    prepared.forEach((mine, index) => {
        const issued = issuedList[index];
        if (issued && issued.email.trim().toLowerCase() === mine.payload.email.trim().toLowerCase()) {
            links.push({
                email: issued.email,
                link: buildAcceptLink(origin, issued.invitationUuid, mine.rootToken, mine.payload.wrapSalt),
                expiresAt: issued.expiresAt,
            });
        } else {
            missingLinks.push(mine.payload.email);
        }
    });
    return { status: 200, sharedResearchers: data.sharedResearchers, links, missingLinks };
}

export type InvitationLinkState = "invalid" | "valid" | "expired" | "used" | "error";

export interface InvitationStatus {
    state: InvitationLinkState;
    email?: string;
    investigationName?: string;
    investigationUuid?: string;
    inviterName?: string;
    expiresAt?: string;
    needsAccount?: boolean;
    /** Only for a valid link: the material this link opens - the v1 key and the KEKs. */
    keyResearcherInvestigation?: string;
    wrappedKeks?: WrappedKekEntry[];
}

async function proofOf(fragment: AcceptFragment): Promise<string> {
    return (await deriveInvitationSecrets(fragment.rootToken, fragment.wrapSalt)).authProof;
}

/**
 * Where this link stands. `error` means the API could not be asked (offline, 5xx) - it must not be
 * shown as a broken link, because the link may be fine.
 */
export async function fetchInvitationStatus(fragment: AcceptFragment): Promise<InvitationStatus> {
    try {
        const response = await axios.post(`${API()}/researcher/invitation/status`, {
            invitationId: fragment.invitationId,
            authProof: await proofOf(fragment),
        });
        if (response.status !== 200 || !response.data || !response.data.invitation) {
            return { state: "error" };
        }
        return response.data.invitation;
    } catch (error) {
        return { state: "error" };
    }
}

/** The fields an invited registration adds to the usual register body (SHER-997 on this line). */
export async function withInvitationProof<T extends object>(registerPayload: T, fragment: AcceptFragment): Promise<T & { invitationId: string; authProof: string }> {
    return { ...registerPayload, invitationId: fragment.invitationId, authProof: await proofOf(fragment) };
}

/**
 * The signed-in researcher's PDK, from what sign-in keeps in this tab (the same two values
 * `answerRequest`'s re-wrap reads). Null when either is missing - a session without them cannot
 * re-wrap anything and must sign in again.
 */
async function ownPdk(storage: Storage = window.localStorage): Promise<CryptoKey | null> {
    const kdfSalt = storage.getItem("keyDerivationSalt");
    const password = storage.getItem("password");
    if (!kdfSalt || !password) {
        return null;
    }
    return derivePasswordKey(password, fromB64(kdfSalt));
}

/**
 * Open this invitation's KEKs with the link and re-wrap them under `pdk`. Throws ONLY for a link
 * that does not open them (AES-GCM authenticates - a wrong key never yields bytes). The raw KEKs
 * are returned for the caller to keep once the server has taken the re-wraps.
 */
async function rewrapInvitationKeks(wrapKey: CryptoKey, invitationKeks: WrappedKekEntry[], pdk: CryptoKey) {
    const raw: { category: string; kek: Uint8Array }[] = [];
    for (const key of invitationKeks) {
        raw.push({ category: key.category, kek: await unwrapKek(key.nonce, key.ciphertext, wrapKey) });
    }
    const reWrapped: WrappedKekEntry[] = [];
    for (const { category, kek } of raw) {
        reWrapped.push({ category, ...(await wrapKek(kek, pdk)) });
    }
    return { raw, reWrapped };
}

function keepRawKeks(investigationUuid: string, raw: { category: string; kek: Uint8Array }[]) {
    for (const { category, kek } of raw) {
        localStorage.setItem(`kek_${investigationUuid}_${category}`, toB64(kek));
    }
}

export type AcceptResult =
    | { ok: true }
    | { ok: false; reason: "invalid" | "expired" | "used" | "no_personal_key" | "error" };

/**
 * Accept, for an account that already existed: open the v1 key and the invitation's KEKs (both
 * from `/invitation/status`) with the link, re-wrap them under the invitee's own keys, and send the
 * re-wraps with the proof to `/answer/v2`. The API redeems the token and writes the KEKs in one
 * transaction.
 *
 * Every local failure returns BEFORE a request: a link that does not open the material, a session
 * without its keys (`no_personal_key`), or a v1 re-wrap that does not round-trip.
 */
export async function acceptTokenInvitation(params: {
    fragment: AcceptFragment;
    investigationUuid: string;
    wrappedKey: string;
    rawKeyResearcher: string | null;
    invitationKeks: WrappedKekEntry[];
}): Promise<AcceptResult> {
    const { fragment, investigationUuid, wrappedKey, rawKeyResearcher, invitationKeks } = params;
    const hasKeks = invitationKeks.length > 0;
    if (!isArgon2Auth() && !rawKeyResearcher) {
        return { ok: false, reason: "no_personal_key" };
    }
    const pdk = hasKeks ? await ownPdk() : null;
    if (hasKeks && !pdk) {
        return { ok: false, reason: "no_personal_key" };
    }

    let investigationKey: string;
    let authProof: string;
    let rewrapped: Awaited<ReturnType<typeof rewrapInvitationKeks>> | null = null;
    try {
        const secrets = await deriveInvitationSecrets(fragment.rootToken, fragment.wrapSalt);
        investigationKey = await unwrapInvestigationKey(secrets.wrapKey, wrappedKey);
        if (pdk) {
            rewrapped = await rewrapInvitationKeks(secrets.wrapKey, invitationKeks, pdk);
        }
        authProof = secrets.authProof;
    } catch (error) {
        return { ok: false, reason: "invalid" };
    }

    // On this line the v1 key wraps "" for argon2 investigations and rawKeyResearcher is "" for
    // argon2 accounts; the round-trip check below still holds for both.
    const personalWrap = encryptData(investigationKey, rawKeyResearcher || "");
    if (decryptData(personalWrap, rawKeyResearcher || "") !== investigationKey) {
        return { ok: false, reason: "error" };
    }

    const body: Record<string, unknown> = { answer: ACCEPTED, keyInvestigationResearcher: personalWrap, invitationId: fragment.invitationId, authProof };
    if (rewrapped) {
        body.reWrappedKeks = rewrapped.reWrapped;
    }
    try {
        const response = await axios.put(
            `${API()}/researcher/investigation/${investigationUuid}/answer${rewrapped ? "/v2" : ""}`,
            body,
            authHeaders(),
        );
        if (response.status === 200) {
            if (rewrapped) {
                keepRawKeks(investigationUuid, rewrapped.raw);
            }
            return { ok: true };
        }
        const code = response.data && response.data.errorCode;
        return { ok: false, reason: code === 10 ? "invalid" : code === 11 ? "expired" : code === 5 ? "used" : "error" };
    } catch (error) {
        return { ok: false, reason: "error" };
    }
}

/**
 * Ask the inviter for a new link (SHER-804). The invitee cannot re-issue one themselves - a new
 * link means wrapping the KEKs again, and only a member holds them.
 */
export async function requestInvitationResend(fragment: AcceptFragment): Promise<boolean> {
    try {
        const response = await axios.post(`${API()}/researcher/invitation/resend-request`, {
            invitationId: fragment.invitationId,
            authProof: await proofOf(fragment),
        });
        return response.status === 200;
    } catch (error) {
        return false;
    }
}

export interface ResendResult {
    status: number;
    errorCode?: number;
    /** The new link to hand over. Present only on success. */
    link?: InvitationLink;
}

/**
 * Re-issue a still-pending invitation (SHER-804): a fresh `S` and wraps, exactly as at share, sent
 * to the resend endpoint. The API expires the older links, replaces the KEK rows, re-sends the
 * email and answers with the new `invitationUuid`; the new link exists only in the returned value.
 */
export async function resendInvitationWithToken(
    investigationUuid: string,
    email: string,
    rawKeyInvestigation: string,
    origin: string,
    keks: RawKeks = {},
): Promise<ResendResult> {
    const { payload, rootToken } = await prepareTokenInvitee({ email, roleId: 0 }, rawKeyInvestigation, keks);
    const { roleId, ...body } = payload;
    void roleId;
    const response = await axios.post(`${API()}/researcher/investigation/${investigationUuid}/invitation/resend`, body, authHeaders());
    const data = response.data || {};
    if (response.status !== 200 || !data.invitation) {
        return { status: response.status, errorCode: data.errorCode };
    }
    return {
        status: 200,
        link: {
            email: data.invitation.email,
            link: buildAcceptLink(origin, data.invitation.invitationUuid, rootToken, payload.wrapSalt),
            expiresAt: data.invitation.expiresAt,
        },
    };
}

export interface InvitedAccount {
    email: string;
    name: string;
    surnames: string;
    password: string;
    language: string;
    cloudflareToken: string;
}

/**
 * Create the invitee's account (SHER-997's invited branch on this line). Same key material as the
 * regular sign-up in components/register.jsx for the configured auth method - argon2: a loginSecret
 * and a key-derivation salt to `/register/v2`; sha256: a personal key under the password to
 * `/register`. The invitation proof is what authorises claiming the placeholder.
 *
 * argon2 also sends the invitation's KEKs (from `/invitation/status`) opened with the link and
 * re-wrapped under the new account's PDK - PBKDF2 of the password with the salt it registers - so
 * registerV2, which accepts the membership, writes them in the same transaction. A link that does
 * not open them returns `invalid` before anything is sent.
 */
export async function registerInvitedResearcher(account: InvitedAccount, fragment: AcceptFragment, invitationKeks: WrappedKekEntry[] = []): Promise<{ ok: boolean; status: number; invalid?: boolean }> {
    const hashed = await hashPassword(account.password);
    const base: Record<string, unknown> = {
        email: account.email,
        name: account.name,
        surnames: account.surnames,
        language: account.language,
        cloudflareToken: account.cloudflareToken,
        [hashed.field]: hashed.value,
    };
    if (isArgon2Auth()) {
        const { wrappedKeks, keyDerivationSalt } = await generateAndWrapKeks(account.password);
        base.wrappedKeks = wrappedKeks;
        base.keyDerivationSalt = keyDerivationSalt;
        if (invitationKeks.length > 0) {
            try {
                const { wrapKey } = await deriveInvitationSecrets(fragment.rootToken, fragment.wrapSalt);
                const pdk = await derivePasswordKey(account.password, fromB64(keyDerivationSalt));
                base.reWrappedKeks = (await rewrapInvitationKeks(wrapKey, invitationKeks, pdk)).reWrapped;
            } catch (error) {
                return { ok: false, status: 0, invalid: true };
            }
        }
    } else {
        base.keyEncrypted = encryptData(await generateKey(), account.password);
    }
    const payload = await withInvitationProof(base, fragment);
    try {
        const response = await axios.post(`${API()}/researcher${hashed.registerEndpoint}`, payload);
        return { ok: response.status === 200, status: response.status };
    } catch (error) {
        return { ok: false, status: 0 };
    }
}
